import classes from "./PieChartWithSectors.module.css";
import useTheme from "../../../../common/hooks/useTheme";
import { PieChart, Pie, Cell } from "recharts";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import {
  getBiomarkerCategoryColor,
  getNutritionProgrammingCategoryColor,
} from "../../store/helper";
import InfoIcon from "../../svg/InfoIcon";
import React from "react";
import { Tooltip } from "@mui/material";
import styled from "@emotion/styled";
import CustomTooltip from "./CustomTooltip";
import { macroAnalysisChartData } from "../../../../providers/dataPlatformReportTypes";

type renderCustomizedLabelProps = {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius?: number;
  outerRadius?: number;
  label: string;
};

type Props = {
  chartData: macroAnalysisChartData;
};

const PieChartWithSectors = ({ chartData }: Props) => {
  const theme = useTheme();

  const sortedData = chartData.pieData
    .slice()
    .sort((a, b) => a.sectorType - b.sectorType);

  const totalValue = sortedData.reduce((acc, curr) => acc + curr.value, 0);

  // Step 1: Calculate the raw percentage for each sector
  const sectorDataWithPercentage = chartData.sectorNames.map((el) => {
    const sectorValue = sortedData
      .filter((item) => item.sectorType === el.type)
      .reduce((acc, curr) => acc + curr.value, 0);

    const percentage = totalValue > 0 ? (sectorValue / totalValue) * 100 : 0;

    return {
      label: el.label,
      value: sectorValue,
      sectorType: el.type,
      rawPercentage: percentage, // Store the raw percentage for later rounding
    };
  });

  // Step 2: Round the raw percentages to 2 decimal places
  let roundedPercentages = sectorDataWithPercentage.map((el) => {
    const roundedPercentage = Math.round(el.rawPercentage * 100) / 100; // Round to 2 decimal places
    return {
      ...el,
      percentage: roundedPercentage,
    };
  });

  // Step 3: Calculate the sum of rounded percentages
  const sumRoundedPercentagesSector = roundedPercentages.reduce(
    (acc, curr) => acc + curr.percentage,
    0
  );

  // Step 4: Adjust the last percentage to ensure the sum equals 100
  const differenceSector = 100 - sumRoundedPercentagesSector;
  if (roundedPercentages.length > 0) {
    roundedPercentages[roundedPercentages.length - 1].percentage +=
      differenceSector;
  }

  // Final result: `sectorNormalizedData` with adjusted percentages
  const sectorNormalizedData = roundedPercentages;

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius = 0,
    label,
  }: renderCustomizedLabelProps) => {
    const radius = outerRadius + 18; // Adjust this value to position the labels further out
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill={theme.colors_ds.NEUTRAL_TEXT_STRONG}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        className={classes.pieChartLabel}
      >
        {label}
      </text>
    );
  };

  const renderCustomizedSectorLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius = 0,
    label,
  }: renderCustomizedLabelProps) => {
    const radius = innerRadius - 25; // Adjust this value to position the labels further in

    // Split the label into words
    const words = label.split(" ");

    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill={theme.colors_ds.NEUTRAL_TEXT_STRONG}
        textAnchor={x < cx ? "start" : "end"}
        dominantBaseline="central"
        className={classes.pieChartSectorLabel}
      >
        {words.map((lineText, i) => (
          <tspan key={i} x={x} dy={i === 0 ? "0em" : "1.2em"}>
            {lineText}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <div className={classes.container}>
      <div
        className={classes.title}
        style={{ color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT }}
      >
        {chartData.title}
      </div>
      <div className={classes.inner}>
        <div
          className={classes.pieChartContainer}
          style={{
            borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
            backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
          }}
        >
          <PieChart width={500} height={349}>
            <Pie
              data={sortedData}
              startAngle={90}
              endAngle={450}
              innerRadius={110}
              outerRadius={130}
              paddingAngle={1.3}
              dataKey="value"
              stroke="transparent"
              label={renderCustomizedLabel}
              labelLine={false}
            >
              {sortedData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getNutritionProgrammingCategoryColor(
                    entry.sectorType,
                    theme
                  )}
                />
              ))}
            </Pie>
            <Pie
              data={sectorNormalizedData}
              startAngle={90}
              endAngle={450}
              innerRadius={98}
              outerRadius={105}
              paddingAngle={2}
              dataKey="value"
              stroke="transparent"
              label={renderCustomizedSectorLabel}
              labelLine={false}
            >
              {sectorNormalizedData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getNutritionProgrammingCategoryColor(
                    entry.sectorType,
                    theme
                  )}
                />
              ))}
            </Pie>
          </PieChart>
        </div>
        {chartData.sectorNames.map((sector, index) => (
          <div
            key={index}
            className={classes.pieChartLegendContainer}
            style={{
              borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
            }}
          >
            <div className={classes.pieChartLegendSectorTitleContainer}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {/*{sector?.tooltipMessage && (*/}
                {/*<CustomTooltip message={sector?.tooltipMessage || ""}>*/}
                {/*  <InfoIcon />*/}
                {/*</CustomTooltip>*/}
                {/*)}*/}
                <div
                  className={classes.pieChartLegendSectorTitle}
                  style={{ color: theme.colors_ds.NEUTRAL_TEXT_STRONG }}
                >
                  {sector.label}
                </div>
              </div>
              <div
                style={{
                  fontFamily: "var(--font-Bold)",
                  fontSize: "14px",
                  color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
                }}
              >
                {
                  sectorNormalizedData.find((el) => el.label === sector.label)
                    ?.percentage
                }
                <span
                  style={{
                    fontFamily: "var(--font-Medium)",
                  }}
                >
                  %
                </span>
              </div>
            </div>

            {sortedData
              .filter((el) => el.sectorType === sector.type)
              .map((entry, index) => (
                <div
                  key={index}
                  className={classes.pieChartLegendItem}
                  style={{
                    borderBottomColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
                  }}
                >
                  <div
                    className={classes.dataPoint}
                    style={{
                      backgroundColor: getNutritionProgrammingCategoryColor(
                        entry.sectorType,
                        theme
                      ),
                    }}
                  />
                  <div
                    className={classes.text}
                    style={{ color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT }}
                  >
                    {entry.label}
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PieChartWithSectors;
